import type { ListItem } from "../../components/List/List"
import { MealEntity } from "../MealsAndDrinks/MealEntity"
import { MemoryClass } from "./MemoryClass"
import { convertToLocalMoney } from "../Generics/Helps"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import i18next from "i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUtensils } from "@fortawesome/free-solid-svg-icons"
import { EProductOrderModeType } from "../../Enum/EProductOrderModeType"
import {
  formatReservationDays,
  formatReservationTimes,
  toReservationModeLabel,
} from "../../Extensions/ReservationExtensions"

export class MealsMemory extends MemoryClass<MealEntity> {
  setList = (mealsList: MealEntity[]) => {
    this.listData = mealsList
  }

  protected cloneEntity(item: MealEntity): MealEntity {
    return new MealEntity({ ...item })
  }

  getListCards = (all: boolean): ListItem[] => {
    const cards: ListItem[] = []
    let count = 0

    this.listData.forEach((ss) => {
      if (all || ss.genericFoodEntity.status === EEnableStatusType.Active) {
        cards.push(this.getCard(ss, count))
      }

      count++
    })

    return cards
  }

  protected getItemId(item: MealEntity): number {
    return item.id
  }

  getCard(ss: MealEntity, count: number): ListItem {
    const enable = ss.genericFoodEntity.status === EEnableStatusType.Active

    const food = ss.genericFoodEntity

    const hasImage = (food.iconUrl ?? "").trim().length > 0
    const tagsCount = food.tags?.length ?? 0
    const additionalsCount = food.allowedAddtionals?.length ?? 0
    const freeAdditionalsCount = food.freeAllowedAddtionals?.length ?? 0
    const selectionsCount = food.allowedSelections?.length ?? 0

    return {
      isDisable: !enable,
      id: ss.id,
      canUse: food.canUse(),
      title: food.name,
      linkImage: food.iconUrl,
      index: count,
      dataFilter: [
        food.name,
        food.status.toEnableStatusTypeString(),
        `${tagsCount} tag(s)`,
        `${additionalsCount} adicional(is)`,
        `${freeAdditionalsCount} grátis`,
      ],
      children: (
        <div
          className={`
          flex justify-between gap-4
          p-4
          rounded-2xl
          transition-all duration-300
          ${enable ? "bg-gradient-to-r from-orange-50 to-white hover:shadow-lg hover:scale-[1.01]" : "bg-gray-100 opacity-70"}
        `}
        >
          <div className="flex gap-4 flex-1">
            {!hasImage && (
              <div className="w-11 h-11 rounded-xl bg-orange-200 text-orange-700 flex items-center justify-center shrink-0">
                <FontAwesomeIcon icon={faUtensils} />
              </div>
            )}

            <div className="flex flex-col justify-between flex-1">
              <span className="font-extrabold text-gray-900 text-base">{food.name}</span>

              <span className="text-sm text-gray-700 line-clamp-2">
                {food.description || i18next.t("components:label_no_description")}
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end justify-between ">
            {enable ? (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Ativo</span>
            ) : (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-600">Inativo</span>
            )}

            <span className="text-lg font-bold text-gray-800 mt-2">{convertToLocalMoney(food.valueBase)}</span>
          </div>
        </div>
      ),
      body: (
        <div className="flex gap-2 flex-wrap mt-2 p-2">
          {food.promotionsIds.length > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
              {i18next.t("components:label_promotion")}
            </span>
          )}

          {tagsCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 font-medium">
              🏷️ {tagsCount} tag(s)
            </span>
          )}

          {additionalsCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
              ➕ {additionalsCount} adicional(is)
            </span>
          )}

          {freeAdditionalsCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-700 font-medium">
              🎁 {freeAdditionalsCount} grátis
            </span>
          )}

          {selectionsCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700 font-medium">
              🧩 {selectionsCount} seleção(ões)
            </span>
          )}

          {food.needKitchen && (
            <span className="px-2 py-1 text-xs rounded-full bg-rose-100 text-rose-700 font-medium">👨‍🍳 Cozinha</span>
          )}

          {food.hasPreparationTime && food.preparationTimeMinutes > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
              {i18next.t("components:label_preparation_time_minutes_short", {
                minutes: food.preparationTimeMinutes,
              })}
            </span>
          )}

          {food.hasStockControl && (
            <span
              className={`px-2 py-1 text-xs rounded-full font-bold
                    ${food.stockQuantity > 0 ? "ring-1 ring-orange-300 bg-orange-100 text-orange-500" : "bg-red-100 text-red-600"}`}
            >
              {food.stockQuantity > 0 ? `${food.stockQuantity} em estoque` : i18next.t("components:label_sold_out")}
            </span>
          )}

          {food.categoryId !== 0 && food.category && (
            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium">
              {food.category.name}
            </span>
          )}

          {food.productOrderMode !== EProductOrderModeType.ImmediateOnly && (
            <div className="mt-2 w-full border-t border-orange-100 pt-2 text-[11px] font-semibold text-slate-600">
              📅 Reserva: {toReservationModeLabel(food.productOrderMode)} • Dias:{" "}
              {formatReservationDays(food.reservationAllowedDays)} • Horários:{" "}
              {formatReservationTimes(food.reservationAllowedTimes)}
            </div>
          )}
        </div>
      ),
    }
  }
}
